import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MessageSquareIcon,
  TrashIcon,
  EyeIcon,
  SearchIcon,
  DownloadIcon,
  ChevronDownIcon,
  ArrowUpRightIcon,
  BarChart3Icon,
  UsersIcon,
  CopyIcon,
  CheckIcon,
  Loader2Icon,
  ClockIcon,
  InboxIcon,
} from 'lucide-react';
import { tenantApi, Conversation } from '../lib/tenant-api';
import { PageLoader, Pagination } from '../components/common';
import { useLocation } from 'react-router-dom';
import AIInsightsDrawer, { AiIcon } from '../components/AIInsightsDrawer';
import DateRangePicker from '../components/DateRangePicker';
import { stringToDateArray } from '../lib/string.lib';

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

function getInitials(identifier: string): string {
  if (identifier.includes('@')) {
    const parts = identifier.split('@')[0].split(/[._-]/);
    return parts.map(p => p[0]?.toUpperCase() || '').join('').substring(0, 2);
  }
  if (identifier.startsWith('session_')) return 'S';
  if (identifier.startsWith('client_')) return 'C';
  if (identifier.startsWith('ip_')) return 'IP';
  return identifier.substring(0, 2).toUpperCase();
}

/** Deterministic avatar colour derived from the identifier string. */
function getAvatarColor(identifier: string): { bg: string; text: string } {
  const palette: { bg: string; text: string }[] = [
    { bg: 'bg-indigo-100', text: 'text-indigo-700' },
    { bg: 'bg-violet-100', text: 'text-violet-700' },
    { bg: 'bg-blue-100', text: 'text-blue-700' },
    { bg: 'bg-teal-100', text: 'text-teal-700' },
    { bg: 'bg-emerald-100', text: 'text-emerald-700' },
    { bg: 'bg-rose-100', text: 'text-rose-700' },
  ];
  let hash = 0;
  for (let i = 0; i < identifier.length; i++) hash = identifier.charCodeAt(i) + ((hash << 5) - hash);
  return palette[Math.abs(hash) % palette.length];
}

function getDisplayName(identifier: string): { name: string; detail: string } {
  if (identifier.includes('@')) {
    const emailParts = identifier.split('@')[0].split(/[._-]/);
    const name = emailParts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
    return { name, detail: identifier };
  }
  if (identifier.startsWith('session_')) {
    return { name: 'Anonymous user', detail: `Session ${identifier.substring(8, 16)}…` };
  }
  if (identifier.startsWith('client_')) {
    return { name: 'Anonymous user', detail: `Client ${identifier.substring(7, 15)}…` };
  }
  if (identifier.startsWith('ip_')) {
    return { name: 'Anonymous user', detail: `IP ${identifier.substring(3)}` };
  }
  return { name: identifier, detail: 'Not available' };
}

function getStatusBadge(status: string) {
  const s = status?.toLowerCase() || '';
  if (s === 'active') return { label: 'Active', className: 'bg-green-50 text-green-700 border border-green-200' };
  if (s === 'abandoned') return { label: 'Abandoned', className: 'bg-orange-50 text-orange-700 border border-orange-200' };
  if (s === 'converted') return { label: 'Converted', className: 'bg-violet-50 text-violet-700 border border-violet-200' };
  if (s === 'closed') return { label: 'Closed', className: 'bg-gray-100 text-gray-600 border border-gray-200' };
  return { label: status || 'Unknown', className: 'bg-gray-100 text-gray-500 border border-gray-200' };
}

function formatDuration(createdAt: string, lastActivity: string): string {
  const start = new Date(createdAt).getTime();
  const end = new Date(lastActivity).getTime();
  const diff = Math.max(0, end - start);
  const totalSeconds = Math.floor(diff / 1000);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function timeAgo(dateString: string): string {
  const diff = Date.now() - new Date(dateString).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'Just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getLastMessage(conversation: Conversation): string | null {
  const msgs = conversation.messages;
  if (!msgs || msgs.length === 0) return null;
  // prefer last user message, else last message overall
  const userMsgs = msgs.filter(m => m.role === 'user');
  const last = userMsgs.length > 0 ? userMsgs[userMsgs.length - 1] : msgs[msgs.length - 1];
  return last.content.trim();
}

/** Value to put on the clipboard for widget session correlation. */
function getSessionIdToCopy(conversation: Conversation): string | null {
  const sid = conversation.session_id?.trim();
  if (sid) return sid;
  if (conversation.user_identifier.startsWith('session_')) {
    return conversation.user_identifier.slice('session_'.length);
  }
  return null;
}

/** Build the initial date-range state from the DateRangePicker's default (last 7 days). */
function getDefaultDateRange(): { dateFrom: string; dateTo: string; rangeLabel: string } {
  const [start, end] = stringToDateArray('last_7_days');
  return {
    dateFrom: start.toISOString(),
    dateTo: end.toISOString(),
    rangeLabel: 'last_7_days',
  };
}

const AI_SUGGESTIONS = [
  'What is the most common question for the week?',
  'Which product is mentioned more?',
  'What are the top user concerns?',
];

// ---------------------------------------------------------------------------
// Export helpers
// ---------------------------------------------------------------------------

const DIVIDER_MAJOR = '='.repeat(64);
const DIVIDER_MINOR = '─'.repeat(64);

function buildTxtExport(
  conversations: Conversation[],
  dateFrom: string | undefined,
  dateTo: string | undefined
): string {
  const exportedAt = new Date().toLocaleString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  const rangeLabel =
    dateFrom && dateTo
      ? `${new Date(dateFrom).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} – ${new Date(dateTo).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`
      : 'All time';

  const lines: string[] = [
    DIVIDER_MAJOR,
    'CONVERSATIONS EXPORT',
    `Exported:       ${exportedAt}`,
    `Date Range:     ${rangeLabel}`,
    `Total Exported: ${conversations.length}`,
    DIVIDER_MAJOR,
  ];

  conversations.forEach((c, idx) => {
    const { name, detail } = getDisplayName(c.user_identifier);
    const badge = getStatusBadge(c.status);
    const msgCount = c.messages?.length ?? 0;

    lines.push('');
    lines.push('');
    lines.push(DIVIDER_MINOR);
    lines.push(`CONVERSATION #${idx + 1}`);
    lines.push(DIVIDER_MINOR);
    lines.push(`ID:             ${c.id}`);
    lines.push(`User:           ${name}${detail && detail !== 'Not available' ? ` (${detail})` : ''}`);
    lines.push(`Status:         ${badge.label}`);
    lines.push(`Created:        ${formatDate(c.created_at)}`);
    lines.push(`Last Activity:  ${formatDate(c.last_activity)}`);
    lines.push(`Duration:       ${formatDuration(c.created_at, c.last_activity)}`);
    lines.push(`Messages:       ${msgCount}`);

    if (msgCount > 0 && c.messages) {
      lines.push('');
      lines.push('MESSAGES');
      lines.push('─'.repeat(12));
      c.messages.forEach((msg) => {
        const role = msg.role === 'user' ? 'USER' : `ASSISTANT${msg.agent_name ? ` (${msg.agent_name})` : ''}`;
        lines.push('');
        lines.push(`[${formatDate(msg.created_at)}] ${role}`);
        const words = msg.content.split(' ');
        let line = '';
        words.forEach((word) => {
          if ((line + ' ' + word).trim().length > 100) {
            lines.push(line.trim());
            line = word;
          } else {
            line = line ? `${line} ${word}` : word;
          }
        });
        if (line.trim()) lines.push(line.trim());
      });
    } else {
      lines.push('');
      lines.push('MESSAGES');
      lines.push('─'.repeat(12));
      lines.push('(no messages)');
    }
  });

  lines.push('');
  lines.push('');
  lines.push(DIVIDER_MAJOR);
  lines.push(`END OF EXPORT — ${conversations.length} conversation(s)`);
  lines.push(DIVIDER_MAJOR);

  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Conversations: React.FC = () => {
  const queryClient = useQueryClient();
  const location = useLocation();

  const defaultRange = getDefaultDateRange();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [copiedConversationId, setCopiedConversationId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [pendingStatus, setPendingStatus] = useState('');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  // Date range — driven by DateRangePicker
  const [dateFrom, setDateFrom] = useState<string | undefined>(defaultRange.dateFrom);
  const [dateTo, setDateTo] = useState<string | undefined>(defaultRange.dateTo);
  const [dateRangeLabel, setDateRangeLabel] = useState<string>(defaultRange.rangeLabel);

  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [detailTab, setDetailTab] = useState<'overview' | 'messages' | 'events'>('overview');
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [showAIChat, setShowAIChat] = useState(false);
  const [initialAIMessage, setInitialAIMessage] = useState<string | undefined>(undefined);
  const [isExporting, setIsExporting] = useState(false);

  const statusDropdownRef = useRef<HTMLDivElement>(null);

  // Click outside to close status dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const limit = 10;
  const offset = ((currentPage ?? 1) - 1) * limit;
  const dateRange = useMemo(() => ({ date_from: dateFrom, date_to: dateTo }), [dateFrom, dateTo]);

  // Handle DateRangePicker changes
  const handleDateRangeChange = (rangeStr: string, startDate?: Date, endDate?: Date) => {
    setDateRangeLabel(rangeStr);
    if (!startDate || !endDate) return;
    setDateFrom(startDate.toISOString());
    setDateTo(endDate.toISOString());
    setCurrentPage(1);
  };

  // Fetch conversations
  const { data: conversationsData, isLoading, error } = useQuery({
    queryKey: ['conversations', currentPage, appliedSearch, dateRange.date_from, dateRange.date_to],
    queryFn: () =>
      tenantApi.getConversations(
        limit,
        offset,
        undefined,
        appliedSearch || undefined,
        dateRange.date_from,
        dateRange.date_to
      ),
  });

  // Batch-fetch conversation details (with messages) for each conversation in the page
  const conversationIds = useMemo(
    () => (conversationsData?.conversations || []).map((c) => c.id),
    [conversationsData]
  );

  const { data: enrichedConversations } = useQuery({
    queryKey: ['conversations-enriched', ...conversationIds],
    queryFn: async () => {
      if (conversationIds.length === 0) return [];
      const results = await Promise.all(
        conversationIds.map((id) => tenantApi.getConversation(id).catch(() => null))
      );
      return results.filter(Boolean) as Conversation[];
    },
    enabled: conversationIds.length > 0,
    keepPreviousData: true,
  });

  // Merge: use enriched data if available, fallback to list data
  const allConversations = useMemo(() => {
    const listConvos = conversationsData?.conversations || [];
    if (!enrichedConversations || enrichedConversations.length === 0) return listConvos;
    const enrichedMap = new Map(enrichedConversations.map((c) => [c.id, c]));
    return listConvos.map((c) => enrichedMap.get(c.id) || c);
  }, [conversationsData, enrichedConversations]);

  // Client-side status + full-text filter
  const conversations = useMemo(() => {
    let results = allConversations;

    if (statusFilter) {
      results = results.filter((c) => c.status?.toLowerCase() === statusFilter.toLowerCase());
    }

    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      results = results.filter((c) => {
        const { name, detail } = getDisplayName(c.user_identifier);
        if (
          name.toLowerCase().includes(q) ||
          detail.toLowerCase().includes(q) ||
          c.user_identifier.toLowerCase().includes(q)
        ) return true;
        if ((c.messages || []).some((m) => m.content.toLowerCase().includes(q))) return true;
        return false;
      });
    }

    return results;
  }, [allConversations, statusFilter, searchTerm]);

  // Fetch conversation stats
  const { data: stats } = useQuery({
    queryKey: ['conversation-stats', dateRange.date_from, dateRange.date_to],
    queryFn: () => tenantApi.getConversationStats(dateRange.date_from, dateRange.date_to),
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      setAppliedSearch(sessionId);
      setSearchTerm(sessionId);
      setCurrentPage(1);
    }
  }, [location.search]);

  // Delete conversation mutation
  const deleteMutation = useMutation({
    mutationFn: tenantApi.deleteConversation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['conversations-enriched'] });
      queryClient.invalidateQueries({ queryKey: ['conversation-stats'] });
      setSelectedConversation(null);
    },
  });

  const conversationDetail = selectedConversation
    ? allConversations.find((c) => c.id === selectedConversation.id) || selectedConversation
    : null;

  const avgMessages = useMemo(() => {
    if (!enrichedConversations || enrichedConversations.length === 0) return 0;
    const totalMsgs = enrichedConversations.reduce((sum, c) => sum + (c.messages?.length || 0), 0);
    return totalMsgs > 0 ? +(totalMsgs / enrichedConversations.length).toFixed(1) : 0;
  }, [enrichedConversations]);

  const handleSearch = () => {
    setAppliedSearch(searchTerm);
    setCurrentPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleDeleteConversation = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this conversation?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleExportTxt = async () => {
    setIsExporting(true);
    try {
      const allData = await tenantApi.getConversations(1000, 0, undefined, appliedSearch || undefined, dateFrom, dateTo);
      const allIds = (allData?.conversations || []).map((c) => c.id);
      const fullConversations = await Promise.all(allIds.map((id) => tenantApi.getConversation(id).catch(() => null)));
      const valid = fullConversations.filter(Boolean) as Conversation[];
      const txt = buildTxtExport(valid, dateFrom, dateTo);
      const blob = new Blob([txt], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fromLabel = dateFrom ? new Date(dateFrom).toISOString().slice(0, 10) : 'all';
      const toLabel = dateTo ? new Date(dateTo).toISOString().slice(0, 10) : 'now';
      a.download = `conversations-${fromLabel}-to-${toLabel}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) return <PageLoader />;

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">Error loading conversations</div>
      </div>
    );
  }

  const totalCount = stats?.total_conversations ?? conversations.length;

  return (
    <>
      <div className="space-y-6">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl sm:tracking-tight">Conversations</h1>
            <p className="mt-1 text-sm text-gray-500">Monitor and review AI widget conversations with your users.</p>
          </div>
          <div className="flex items-center flex-wrap gap-2">
            <DateRangePicker selectedRange={dateRangeLabel} onDateRangeChange={handleDateRangeChange} />
            <button
              onClick={handleExportTxt}
              disabled={isExporting}
              className="inline-flex items-center gap-2 border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {isExporting ? <Loader2Icon className="h-4 w-4 animate-spin" /> : <DownloadIcon className="h-4 w-4" />}
              {isExporting ? 'Exporting…' : 'Export'}
            </button>
          </div>
        </div>

        {/* ── Stats Cards ────────────────────────────────────────────────── */}
        {stats && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {/* Total Conversations */}
            <div className="bg-white border border-gray-100 shadow-sm p-5 flex items-center gap-4">
              <div className="flex-shrink-0 h-12 w-12 rounded-xl bg-indigo-50 flex items-center justify-center">
                <MessageSquareIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Total Conversations</p>
                <p className="text-3xl font-bold text-gray-900 mt-0.5">{stats.total_conversations}</p>
                <p className="text-xs text-gray-400 mt-0.5">for selected period</p>
              </div>
            </div>
            {/* Unique Users */}
            <div className="bg-white border border-gray-100 shadow-sm p-5 flex items-center gap-4">
              <div className="flex-shrink-0 h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center">
                <UsersIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Unique Users</p>
                <p className="text-3xl font-bold text-gray-900 mt-0.5">{stats.unique_users}</p>
                <p className="text-xs text-gray-400 mt-0.5">distinct identifiers</p>
              </div>
            </div>
            {/* Avg Messages */}
            <div className="bg-white border border-gray-100 shadow-sm p-5 flex items-center gap-4">
              <div className="flex-shrink-0 h-12 w-12 rounded-xl bg-violet-50 flex items-center justify-center">
                <BarChart3Icon className="h-6 w-6 text-violet-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Avg. Messages</p>
                <p className="text-3xl font-bold text-gray-900 mt-0.5">{avgMessages}</p>
                <p className="text-xs text-gray-400 mt-0.5">per conversation</p>
              </div>
            </div>
          </div>
        )}

        {/* ── AI Insights Section ─────────────────────────────────────────── */}
        <div className="bg-white border border-gray-100 shadow-sm p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Ask AI About Your Conversations</h2>
              <p className="mt-0.5 text-sm text-gray-500">
                Get instant insights, analytics, and answers about your AI widget performance.
              </p>
            </div>
            <button
              onClick={() => setShowAIChat(true)}
              className="inline-flex items-center justify-center gap-0 bg-gradient-to-br from-indigo-500 to-violet-500 text-white px-5 pl-2 py-1 text-sm font-medium hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-opacity sm:flex-shrink-0"
            >
              <AiIcon />
              Get AI insights
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {AI_SUGGESTIONS.map((suggestion, idx) => (
              <button
                key={idx}
                onClick={() => { setInitialAIMessage(suggestion); setShowAIChat(true); }}
                className="inline-flex items-center justify-between text-left gap-2 px-3 py-1.5 text-sm text-gray-600 bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-colors w-full sm:w-auto"
              >
                {suggestion}
                <ArrowUpRightIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>

        {/* ── Conversation Table ──────────────────────────────────────────── */}
        <div className="bg-white border border-gray-100 shadow-sm">
          {/* Table header bar */}
          <div className="px-5 pt-5 pb-4 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              {/* Search */}
              <div className="relative flex-1">
                <SearchIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search by user, email, session, or message content…"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    if (e.target.value === '') { setAppliedSearch(''); setCurrentPage(1); }
                  }}
                  onKeyDown={handleSearchKeyDown}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:bg-white transition-colors placeholder:text-gray-400"
                />
              </div>
              {/* Status filter */}
              <div className="relative" ref={statusDropdownRef}>
                <button
                  onClick={() => { setShowStatusDropdown(!showStatusDropdown); setPendingStatus(statusFilter); }}
                  className={`flex items-center gap-2 px-3 py-2 text-sm border transition-colors ${statusFilter ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100'}`}
                >
                  <span>{statusFilter ? (statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)) : 'All statuses'}</span>
                  <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400" />
                </button>
                {showStatusDropdown && (
                  <div className="absolute right-0 mt-1.5 w-52 bg-white shadow-xl border border-gray-200 z-20">
                    <div className="p-3">
                      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Filter by status</p>
                      <div className="space-y-1">
                        {[
                          { value: '', label: 'All statuses' },
                          { value: 'active', label: 'Active' },
                          { value: 'abandoned', label: 'Abandoned' },
                          { value: 'converted', label: 'Converted' },
                          { value: 'closed', label: 'Closed' },
                        ].map(({ value, label }) => (
                          <label key={value} className="flex items-center gap-2.5 px-2 py-1.5 cursor-pointer hover:bg-gray-50 rounded">
                            <input
                              type="radio"
                              name="statusFilter"
                              checked={pendingStatus === value}
                              onChange={() => setPendingStatus(value)}
                              className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                            />
                            <span className="text-sm text-gray-700">{label}</span>
                          </label>
                        ))}
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-100 flex justify-end">
                        <button
                          onClick={() => { setStatusFilter(pendingStatus); setShowStatusDropdown(false); setCurrentPage(1); }}
                          className="px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Conversations
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                    {totalCount}
                  </span>
                </h3>
                {(appliedSearch || statusFilter) && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Filtered · showing {conversations.length} of {totalCount}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Empty state */}
          {conversations.length === 0 ? (
            <div className="py-16 px-6 flex flex-col items-center text-center">
              <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <InboxIcon className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-900">No conversations found</h3>
              <p className="mt-1 text-sm text-gray-500 max-w-xs">
                {appliedSearch || statusFilter
                  ? 'No conversations match your current search or filters. Try adjusting them.'
                  : 'Conversations will appear here once users start interacting with your AI assistant.'}
              </p>
              {(appliedSearch || statusFilter) && (
                <button
                  onClick={() => { setSearchTerm(''); setAppliedSearch(''); setStatusFilter(''); setCurrentPage(1); }}
                  className="mt-4 text-sm font-medium text-indigo-600 hover:text-indigo-700 underline-offset-2 hover:underline"
                >
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" style={{ minWidth: 700 }}>
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                      Messages
                    </th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                      Started
                    </th>
                    <th className="px-5 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {conversations.map((conversation) => {
                    const { name, detail } = getDisplayName(conversation.user_identifier);
                    const initials = getInitials(conversation.user_identifier);
                    const avatarColor = getAvatarColor(conversation.user_identifier);
                    const badge = getStatusBadge(conversation.status);
                    const msgCount = conversation.messages?.length || 0;
                    const duration = formatDuration(conversation.created_at, conversation.last_activity);
                    const lastMsg = getLastMessage(conversation);
                    const sessionToCopy = getSessionIdToCopy(conversation);

                    return (
                      <tr
                        key={conversation.id}
                        className="hover:bg-indigo-50/30 transition-colors group cursor-default"
                      >
                        {/* User */}
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3 max-w-xs">
                            <div className={`flex-shrink-0 h-9 w-9 rounded-full ${avatarColor.bg} ${avatarColor.text} flex items-center justify-center text-sm font-bold`}>
                              {initials}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5">
                                <p className="text-sm font-semibold text-gray-900 truncate">{name}</p>
                                {sessionToCopy && (
                                  <button
                                    type="button"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        await navigator.clipboard.writeText(sessionToCopy);
                                        setCopiedConversationId(conversation.id);
                                        globalThis.setTimeout(() => {
                                          setCopiedConversationId((prev) => prev === conversation.id ? null : prev);
                                        }, 2000);
                                      } catch { /* noop */ }
                                    }}
                                    className="flex-shrink-0 p-0.5 text-gray-400 hover:text-indigo-600 rounded transition-colors opacity-0 group-hover:opacity-100"
                                    title="Copy session ID"
                                  >
                                    {copiedConversationId === conversation.id
                                      ? <CheckIcon className="h-3 w-3 text-green-500" />
                                      : <CopyIcon className="h-3 w-3" />}
                                  </button>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 truncate">{detail}</p>
                              {lastMsg && (
                                <p className="text-xs text-gray-400 italic truncate mt-0.5 max-w-[220px]">
                                  "{lastMsg.substring(0, 60)}{lastMsg.length > 60 ? '…' : ''}"
                                </p>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-5 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full ${badge.className}`}>
                            {badge.label}
                          </span>
                        </td>

                        {/* Messages */}
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-1.5">
                            <MessageSquareIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-sm font-semibold text-gray-800">{msgCount}</span>
                          </div>
                          {msgCount > 0 && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              {msgCount === 1 ? 'message' : 'messages'}
                            </p>
                          )}
                        </td>

                        {/* Duration */}
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-1.5">
                            <ClockIcon className="h-3.5 w-3.5 text-gray-400" />
                            <span className="text-sm text-gray-700">{duration}</span>
                          </div>
                        </td>

                        {/* Started */}
                        <td className="px-5 py-4">
                          <p
                            className="text-sm text-gray-700"
                            title={formatDate(conversation.created_at)}
                          >
                            {timeAgo(conversation.created_at)}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {new Date(conversation.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </p>
                        </td>

                        {/* Actions */}
                        <td className="px-5 py-4">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => { setSelectedConversation(conversation); setDetailTab('overview'); setExpandedEvents(new Set()); }}
                              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                              title="View details"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteConversation(conversation.id)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                              title="Delete"
                              disabled={deleteMutation.isLoading}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {conversations.length > 0 && (
            <Pagination
              className="px-5 py-4 border-t border-gray-100"
              page={currentPage}
              totalPages={Math.max(1, Math.ceil(totalCount / limit))}
              onPageChange={setCurrentPage}
              rangeStart={offset + 1}
              rangeEnd={offset + conversations.length}
              totalItems={totalCount}
              itemLabel="conversations"
              hasNextPage={conversations.length >= limit}
              hideWhenSinglePage
            />
          )}
        </div>
      </div>
      {/* ── AI Insights Drawer ──────────────────────────────────────────── */}
      <AIInsightsDrawer
        isOpen={showAIChat}
        onClose={() => { setShowAIChat(false); setInitialAIMessage(undefined); }}
        contextKey="conversations"
        description="Conversations context"
        title="Start a conversation"
        subtitle="Ask me anything about your conversations, analytics, or insights."
        contextParams={dateFrom ? { date_from: dateFrom, date_to: dateTo } : undefined}
        suggestions={AI_SUGGESTIONS}
        initialMessage={initialAIMessage}
      />

      {/* ── Conversation Detail Modal ────────────────────────────────────── */}
      {selectedConversation && conversationDetail && (() => {
        const msgCount = conversationDetail.messages?.length || 0;
        const eventCount = conversationDetail.events?.length || 0;
        const { name, detail } = getDisplayName(conversationDetail.user_identifier);
        const badge = getStatusBadge(conversationDetail.status);
        const avatarColor = getAvatarColor(conversationDetail.user_identifier);
        const initials = getInitials(conversationDetail.user_identifier);

        const tabs: { key: 'overview' | 'messages' | 'events'; label: string; count?: number }[] = [
          { key: 'overview', label: 'Overview' },
          { key: 'messages', label: 'Messages', count: msgCount },
          { key: 'events', label: 'Event logs', count: eventCount },
        ];

        return (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto w-11/12 max-w-3xl shadow-2xl bg-white min-h-[70vh] max-h-[85vh] overflow-y-auto mb-10">
              {/* Modal Header */}
              <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-full ${avatarColor.bg} ${avatarColor.text} flex items-center justify-center text-sm font-bold flex-shrink-0`}>
                    {initials}
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">{name}</h3>
                    <p className="text-xs text-gray-500">{detail}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full ${badge.className}`}>
                    {badge.label}
                  </span>
                  <button
                    onClick={() => setSelectedConversation(null)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex items-center gap-1 px-6 pt-4 border-b border-gray-100 pb-0">
                {tabs.map(({ key, label, count }) => (
                  <button
                    key={key}
                    onClick={() => setDetailTab(key)}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${detailTab === key
                      ? 'border-indigo-600 text-indigo-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    {label}
                    {count !== undefined && (
                      <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${detailTab === key ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}>
                        {count}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {/* Overview */}
                {detailTab === 'overview' && (
                  <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                    {[
                      { label: 'User name', value: name },
                      { label: 'User email / identifier', value: detail },
                      { label: 'Session ID', value: conversationDetail.user_identifier.startsWith('session_') ? `Session ${conversationDetail.user_identifier.substring(8)}` : conversationDetail.user_identifier },
                      { label: 'Created', value: formatDate(conversationDetail.created_at) },
                      { label: 'Last activity', value: formatDate(conversationDetail.last_activity) },
                      { label: 'Duration', value: formatDuration(conversationDetail.created_at, conversationDetail.last_activity) },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex items-center justify-between px-4 py-3 bg-white even:bg-gray-50/50">
                        <span className="text-sm text-gray-500">{label}</span>
                        <span className="text-sm font-medium text-gray-900 text-right max-w-xs truncate">{value}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between px-4 py-3 bg-white">
                      <span className="text-sm text-gray-500">Status</span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full ${badge.className}`}>
                        {badge.label}
                      </span>
                    </div>
                  </div>
                )}

                {/* Messages */}
                {detailTab === 'messages' && (
                  <div>
                    {msgCount > 0 ? (
                      <div className="space-y-3">
                        {[...conversationDetail.messages!].sort((a, b) => a.sequence_number - b.sequence_number).map((message) => {
                          const isUser = message.role === 'user';
                          const senderName = isUser ? name : (message.agent_name || 'Assistant');
                          return (
                            <div key={message.id} className="flex gap-3">
                              <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${isUser ? `${avatarColor.bg} ${avatarColor.text}` : 'bg-indigo-100 text-indigo-700'}`}>
                                {isUser ? initials : 'AI'}
                              </div>
                              <div className="flex-1 flex flex-col gap-1">
                                <p className={`text-xs font-semibold ${isUser ? 'text-orange-600' : 'text-indigo-600'}`}>{senderName}</p>
                                <div className={`px-4 py-2.5 text-sm text-gray-800 whitespace-pre-wrap ${isUser ? 'bg-orange-50 border border-orange-100' : 'bg-gray-100 border border-gray-200'}`}>
                                  {message.content}
                                </div>
                                <p className="text-[11px] text-gray-400">{formatDate(message.created_at)}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-500 text-sm">No messages in this conversation</div>
                    )}
                  </div>
                )}

                {/* Events */}
                {detailTab === 'events' && (
                  <div>
                    {eventCount > 0 ? (
                      <div className="space-y-3">
                        {conversationDetail.events!.map((event) => {
                          const isExpanded = expandedEvents.has(event.id);
                          const dataStr = event.extra_data ? JSON.stringify(event.extra_data, null, 2) : '';
                          const isLong = dataStr.length > 120;
                          const displayData = isLong && !isExpanded ? dataStr.substring(0, 120) + '...' : dataStr;

                          return (
                            <div key={event.id} className="border border-gray-200 bg-gray-50 p-4 rounded">
                              <div className="flex items-center gap-2 mb-1.5">
                                <span className={`text-sm font-semibold ${event.generation_type === 'user' ? 'text-orange-600' : 'text-indigo-600'}`}>
                                  {event.event_type}
                                </span>
                                <span className="inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full bg-white border border-gray-300 text-gray-500">
                                  {event.generation_type === 'user' ? 'User' : 'System'}
                                </span>
                              </div>
                              <p className="text-xs text-gray-400 mb-2">{formatDate(event.timestamp)}</p>
                              {dataStr && (
                                <div className="text-xs text-gray-600 font-mono bg-white p-3 border border-gray-200 whitespace-pre-wrap break-all rounded">
                                  {displayData}
                                  {isLong && (
                                    <button
                                      onClick={() => {
                                        const next = new Set(expandedEvents);
                                        if (isExpanded) next.delete(event.id); else next.add(event.id);
                                        setExpandedEvents(next);
                                      }}
                                      className="ml-1 text-indigo-600 hover:text-indigo-800 font-sans font-medium"
                                    >
                                      {isExpanded ? 'See less' : 'See more'}
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-500 text-sm">No session event logs in this conversation</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
};

export default Conversations;
